import { useContext, useEffect, useState } from "react";

import SimpleModal from "../../../../../shared/components/simple-modal/simple-modal";
import Spinner from "../../../../../shared/components/spinner/spinner";
import AuthContext from "../../../../../shared/context/auth-context";
import { IEnumerationOption } from "../../../../../shared/definitions/datatypes";
import useHttp from "../../../../../shared/hooks/use-http";
import { useHttpGet } from "../../../../../shared/hooks/use-httpNonTable";
import { getAllData, getData } from "../../../../../shared/utils/api";
import { backendDataTypeToFrontend, IObject, validateLoadingStatusNew } from "../../../../../shared/utils/utils";
import { noop } from "../../../../utils";
import ColumnSelector from "../../../column-selector/column-selector";
import SearchInput from "../../../search-input/search-input";
import { IWidgetModalProps } from "../../widget-utils";
import "./datamodel-related-alerted-event-modal.scss";
import { IColumn } from "../../../data-table/data-table";
import { Widget } from "../../widget-utils";
import { IRelatedAlertedEventWidget } from "../../widgets/related-alerted-event-widget/related-alerted-event-widget";

export interface IDatamodelRelatedAlertedEventModal {
  type: "datamodelRelatedAlertedEvent";
}

type IDatamodelRelatedAlertedEventModalProps = IWidgetModalProps<Widget & IRelatedAlertedEventWidget>

const DatamodelRelatedAlertedEventModal = ({modalOpen, widgetData, onWidgetChanged}: IDatamodelRelatedAlertedEventModalProps) => {
  const authCtx = useContext(AuthContext);
  
  const [showModal, setShowModal] = useState(modalOpen || false);
  const [events, setEvents] = useState<IObject[]>([]);
  const [columns, setColumns] = useState<IColumn[]>([]);
  const [selectedColumns, setSelectedColumns] = useState<IColumn[]>([]);
  const [selectedEventId, setSelectedEventId] = useState<string | undefined>();
  
  const { sendRequest: getEvents, status: getAllEventsStatus, data: allEventData, error: getEventError } = useHttp(getAllData, true, authCtx.token, undefined, "manager");
  const { sendRequest: getDatamodelDetails, data: getDatamodelData, status: getDatamodelStatus, error: getDatamodelError } = useHttpGet(getData, false, authCtx.token, undefined, "platform");

  useEffect(() => {
    if (widgetData?.id) {
      setSelectedEventId(widgetData.datamodelId);
      setSelectedColumns(widgetData.selectedColumns);
    }
  }, [widgetData]);

  useEffect(() => {
    setShowModal(modalOpen || false);
  }, [modalOpen]);

  useEffect(() => {
    getEvents("available_datamodels/event");
  }, []);

  useEffect(() => {
    if (allEventData && !getEventError && getAllEventsStatus === "completed") {
      setEvents(allEventData.rows);
    }
  }, [getAllEventsStatus, allEventData, getEventError]);

  useEffect(() => {
    if (selectedEventId) {
      getDatamodelDetails(`datamodel/event/${selectedEventId}`);
    }
  }, [selectedEventId]);

  // Process datamodel columns
  useEffect(() => {
    if (getDatamodelStatus === "completed" && !getDatamodelError && getDatamodelData) {
      const columnData = getDatamodelData.fields.map((f: IObject) => ({
        key: f.input_field_name,
        displayName: f.field_name,
        dataType: backendDataTypeToFrontend(f.field_datatype)
      }) as IColumn);
      
      setColumns(columnData);
    }
  }, [getDatamodelData, getDatamodelStatus, getDatamodelError]);

  const handleOnEventChange = (selectedOption: IEnumerationOption | undefined) => {
    setSelectedEventId(selectedOption?.id);
    if (!selectedOption) {
      setSelectedColumns([]);
      setSelectedEventId(undefined);
      setColumns([]);
      return;
    }
  };

  const handleColumnSelectionChanged = (selectedColumns: IColumn[]) => {
    setSelectedColumns(selectedColumns);
  };

  const renderModalBody = () => {
    return <div>
      {validateLoadingStatusNew([getAllEventsStatus]) && <Spinner />}
      <SearchInput
        label="Event"
        handleOnChange={handleOnEventChange}
        data={events.map((event: IObject) => ({ id: event.id, name: event.name }))}
        inputKey="eventId"
        required={true}
        selectedOption={selectedEventId || ""}
      />
      {selectedEventId && (
        <div className="datamodelRelatedAlertedEventModal-columnSelector">
          <ColumnSelector 
            availableColumns={columns} 
            selectedColumns={selectedColumns} 
            onSelectionChanged={handleColumnSelectionChanged} 
          />
        </div>
      )}
    </div>;
  };
  
  const handleModalSubmit = () => {
    if (!selectedEventId || selectedColumns.length === 0) {
      return;
    }

    const selectedEvent = events.find(event => event.id === selectedEventId);
    if (!selectedEvent) {
      return;
    }

    onWidgetChanged({
      id: widgetData?.id || `datamodelRelatedAlertedEvent_${selectedEventId}`,
      modalType: "datamodelRelatedAlertedEvent",
      type: "relatedAlertedEvent",
      label: selectedEvent.name,
      modalData: {
        datamodelType: "event"
      },
      datamodelId: selectedEventId,
      selectedColumns: selectedColumns
    } as Widget & IRelatedAlertedEventWidget);
    
    setShowModal(false);
  };

  const handleModalClosed = () => {
    onWidgetChanged(undefined);
    setShowModal(false);
  };

  const handleAddRelatedAlertedEventClick = () => {
    setShowModal(true);
    setSelectedColumns([]);
    setSelectedEventId(undefined);
    setColumns([]);
  };

  return <div className="datamodelRelatedAlertedEventModal">
    <span className="datamodelRelatedAlertedEventModal-layoutButton" onClick={handleAddRelatedAlertedEventClick}>
      {"Related alerted events +"}
    </span>
    <SimpleModal 
      actionButtonDisabled={selectedColumns.length === 0} 
      mode="Related alerted events" 
      onModalClickHandler={noop} 
      modalBody={renderModalBody()} 
      buttonText={"Add"} 
      handleModalClosed={handleModalClosed} 
      handleSubmit={handleModalSubmit} 
      show={showModal} 
      allowScrollOnBody={false}
    />
  </div>;
};

export default DatamodelRelatedAlertedEventModal;